/**
 * Custom hook for managing chat messages
 * Handles message operations, streaming state, and message-related actions
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { chatService } from '../services/chatService';
import type { ChatMessage, ChatError } from '../types/chat';

export function useChatMessages(conversationId: string) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<ChatError | null>(null);
  const [streamingMessage, setStreamingMessage] = useState<ChatMessage | null>(null);

  // Ref to access current messages without adding to callback dependencies
  const messagesRef = useRef<ChatMessage[]>(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  /**
   * Load messages for a specific conversation
   */
  const loadMessages = useCallback(async (targetConversationId: string) => {
    if (!targetConversationId) {
      setMessages([]);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const conversationMessages = await chatService.getMessages(targetConversationId);
      setMessages(conversationMessages);
      console.log('📨 DEBUG: Loaded messages for conversation:', targetConversationId, conversationMessages);
    } catch (err) {
      const chatError = chatService.handleError(err);
      setError(chatError);
      console.error('Failed to load messages:', chatError);
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Add a message to the current conversation
   */
  const addMessage = useCallback(async (message: ChatMessage) => {
    try {
      setError(null);
      
      // Add message to database
      await chatService.addMessage(message);
      
      // Add to local state
      setMessages(prevMessages => [...prevMessages, message]);
      
      console.log('📝 Added message to conversation:', message.conversationId);
    } catch (err) {
      const chatError = chatService.handleError(err);
      setError(chatError);
      console.error('Failed to add message:', chatError);
    }
  }, []);

  /**
   * Delete a message
   */
  const deleteMessage = useCallback(async (messageId: string) => {
    try {
      setError(null);
      
      await chatService.deleteMessage(messageId);
      
      // Remove from local state
      setMessages(prevMessages => prevMessages.filter(msg => msg.id !== messageId));
      
      console.log('🗑️ Deleted message:', messageId);
    } catch (err) {
      const chatError = chatService.handleError(err);
      setError(chatError);
      console.error('Failed to delete message:', chatError);
    }
  }, []);

  /**
   * Add a streaming message (local state only)
   */
  const addStreamingMessage = useCallback((message: ChatMessage) => {
    setIsStreaming(true);
    setStreamingMessage(message);
    setMessages(prevMessages => [...prevMessages, message]);
  }, []);

  /**
   * Save an existing message to database without modifying local state
   */
  const saveMessageToDatabase = useCallback(async (message: ChatMessage) => {
    try {
      setError(null);
      await chatService.addMessage(message);
      console.log('💾 Saved message to database:', message.id);
    } catch (err) {
      const chatError = chatService.handleError(err);
      setError(chatError);
      console.error('Failed to save message to database:', chatError);
    }
  }, []);

  /**
   * Update streaming message content
   */
  const updateStreamingMessage = useCallback((messageId: string, updates: Partial<ChatMessage>) => {
    setMessages(prevMessages => 
      prevMessages.map(msg => 
        msg.id === messageId 
          ? { ...msg, ...updates }
          : msg
      )
    );
    
    // Update the streaming message reference
    setStreamingMessage(prev => 
      prev && prev.id === messageId 
        ? { ...prev, ...updates }
        : prev
    );
  }, []);

  /**
   * Complete streaming message (save to database)
   */
  const completeStreamingMessage = useCallback(async (messageId: string, finalContent: string) => {
    try {
      setError(null);

      // Find the message using ref to avoid dependency on messages state
      const messageToSave = messagesRef.current.find(msg => msg.id === messageId);

      if (messageToSave) {
        const completedMessage: ChatMessage = {
          ...messageToSave,
          content: finalContent,
          timestamp: new Date(),
        };

        // Save to database
        await chatService.addMessage(completedMessage);

        // Update local state with final content
        setMessages(prevMessages =>
          prevMessages.map(msg =>
            msg.id === messageId ? completedMessage : msg
          )
        );

        console.log('✅ Completed streaming message:', messageId);
      } else {
        console.warn('Message not found in state:', messageId);
      }

      setIsStreaming(false);
      setStreamingMessage(null);
    } catch (err) {
      const chatError = chatService.handleError(err);
      setError(chatError);
      console.error('Failed to complete streaming message:', chatError);

      // Remove the streaming message on error
      setMessages(prevMessages => prevMessages.filter(msg => msg.id !== messageId));
      setIsStreaming(false);
      setStreamingMessage(null);
    }
  }, []);

  /**
   * Cancel streaming message
   */
  const cancelStreamingMessage = useCallback((messageId: string) => {
    setMessages(prevMessages => prevMessages.filter(msg => msg.id !== messageId));
    setIsStreaming(false);
    setStreamingMessage(null);
    console.log('❌ Cancelled streaming message:', messageId);
  }, []);

  /**
   * Retry sending a failed message
   */
  const retryMessage = useCallback(async (messageId: string) => {
    try {
      setError(null);

      // Use ref to avoid dependency on messages state
      const message = messagesRef.current.find(msg => msg.id === messageId);
      if (!message || message.role !== 'user') return;

      // Remove the failed message
      await deleteMessage(messageId);

      // Re-add with new ID
      const retryMsg: ChatMessage = {
        ...message,
        id: crypto.randomUUID(),
        timestamp: new Date(),
      };

      await addMessage(retryMsg);

      console.log('🔄 Retrying message:', messageId, '→', retryMsg.id);

      return retryMsg;
    } catch (err) {
      const chatError = chatService.handleError(err);
      setError(chatError);
      console.error('Failed to retry message:', chatError);
    }
  }, [deleteMessage, addMessage]);

  /**
   * Clear all messages for current conversation
   */
  const clearMessages = useCallback(() => {
    setMessages([]);
    setIsStreaming(false);
    setStreamingMessage(null);
    setError(null);
  }, []);

  /**
   * Get message statistics
   */
  const getMessageStats = useCallback(() => {
    const userMessages = messages.filter(msg => msg.role === 'user').length;
    const assistantMessages = messages.filter(msg => msg.role === 'assistant').length;
    const totalCharacters = messages.reduce((sum, msg) => sum + msg.content.length, 0);
    
    return {
      total: messages.length,
      userMessages,
      assistantMessages,
      totalCharacters,
      hasMessages: messages.length > 0,
    };
  }, [messages]);

  // Load messages when conversation changes
  useEffect(() => {
    if (conversationId) {
      loadMessages(conversationId);
    } else {
      clearMessages();
    }
  }, [conversationId, loadMessages, clearMessages]);

  // Actions object for external use (memoized to prevent re-renders)
  const actions = useMemo(() => ({
    send: addMessage,
    delete: deleteMessage,
    retry: retryMessage,
    addStreaming: addStreamingMessage,
    updateStreaming: updateStreamingMessage,
    completeStreaming: completeStreamingMessage,
    cancelStreaming: cancelStreamingMessage,
    saveToDatabase: saveMessageToDatabase,
    clear: clearMessages,
    reload: () => loadMessages(conversationId),
  }), [
    addMessage,
    deleteMessage,
    retryMessage,
    addStreamingMessage,
    updateStreamingMessage,
    completeStreamingMessage,
    cancelStreamingMessage,
    saveMessageToDatabase,
    clearMessages,
    loadMessages,
    conversationId,
  ]);

  // Memoize return value to prevent unnecessary re-renders in consumers
  const result = useMemo(() => ({
    messages,
    isLoading,
    isStreaming,
    error,
    actions,
    streamingMessage,
    getMessageStats,
  }), [messages, isLoading, isStreaming, error, actions, streamingMessage, getMessageStats]);

  return result;
}